<template>
	<div>
		<div class="d-flex justify-content-between mb-3">
			<h4>Widgets</h4>
			<div class="add-widgets-container">
				<CDropdown
					class="text-right btn-dropdown"
					color="secondary"
					:disabled="!isEditMode"
				>
					<template #toggler-content>
						<CIcon class="mr-2" name="cil-plus" />Create widget
					</template>
					<template>
						<CDropdownItem @click="onClickOpenModal(REFS_HIGHLIGHT_BANNER_WIDGET_MODAL, { type: WIDGET_TYPE.highlight })">
							Highlight banner
						</CDropdownItem>
						<hr>
						<CDropdownItem @click="onClickOpenModal(REFS_PRODUCT_WIDGET_MODAL)">
							Product
						</CDropdownItem>
						<CDropdownItem @click="onClickOpenModal(REFS_WYSIWYG_WIDGET_MODAL)">
							WYSIWYG
						</CDropdownItem>
						<CDropdownItem @click="onClickOpenModal(REFS_CUSTOM_HERO_BANNER_WIDGET_MODAL, { type: WIDGET_TYPE.custom_hero })">
							Custom hero banner
						</CDropdownItem>
						<CDropdownItem @click="onClickOpenModal(REFS_HIGHLIGHT_LOB_WIDGET_MODAL, { type: WIDGET_TYPE.highlight_lob_banner })">
							Highlight LOB banner
						</CDropdownItem>
						<CDropdownItem @click="onClickOpenModal(REFS_LOB_WIDGET_MODAL, { type: WIDGET_TYPE.lob })">
							LOB banner
						</CDropdownItem>
					</template>
				</CDropdown>
				<div v-if="!isEditMode" class="tooltip">You have to create a static page before you can create widgets.</div>
			</div>
		</div>
		<div class="typo-caption-2 mb-2">
			วิดเจ็ตทั้งหมดจะแสดงในหน้าหลัก คุณสามารถสร้างวิดเจ็ต (ยกเว้น Flash Sale ซึ่งจะลิงก์อัตโนมัติ), จัดลำดับวิดเจ็ต และกำหนดการแสดงผล
		</div>
		<CRow>
			<CCol class="table-wrapper">
				<BaseTable
					:fields="WIDGET_FIELDS"
					:items="widgetList"
					class="table-custom widget-table-other"
					is-draggable
					:is-loading="isLoading"
					@drag="handleUpdateOrder"
				>
					<template #no-items-view>
						<div class="empty-table-element">
							<p class="subtitle">
								{{ $t('global.searchNotFound', {field: 'widget'}) }}
							</p>
						</div>
					</template>
					<template #name="{item}">
						<strong>{{ item }}</strong>
					</template>
					<template #linkTo="{item}">
						<a class="link-to" @click="handleLink(item)">
							{{ item.name || 'Add banner' }}
						</a>
						<div v-if="item.status" class="link-status">
							{{ item.status }}
						</div>
					</template>
					<template #websiteVisibility="{item}">
						<CBadge class="badge-status" :color="item.value">
							{{ item.name }}
						</CBadge>
					</template>
					<template #mobileVisibility="{item}">
						<CBadge class="badge-status" :color="item.value">
							{{ item.name }}
						</CBadge>
					</template>
					<template #edit="{item}">
						<div class="text-left">
							<CButton
								v-if="item.editable"
								class="transparent p-0 btn-edit"
								@click="handleEdit(item.id)"
							>
								<CIcon class="icon-pencil" name="cil-pencil" />
							</CButton>
						</div>
					</template>
				</BaseTable>
			</CCol>
		</CRow>
		<ModalProductWidget
			:ref="REFS_PRODUCT_WIDGET_MODAL"
			@onSubmit="onSubmitModal"
			@onRemove="handleRemove"
		/>
		<ModalWYSIWYGWidget
			:ref="REFS_WYSIWYG_WIDGET_MODAL"
			:slug="slug"
			@onSubmit="onSubmitModal"
			@onRemove="handleRemove"
		/>
		<ModalAddWYSIWYG
			:ref="REFS_ADD_WYSIWYG_MODAL"
			:slug="slug"
			@onSubmit="onSubmitModal"
		/>
		<ModalLOBWidget
			:ref="REFS_LOB_WIDGET_MODAL"
			@onSubmit="onSubmitModal"
			@onRemove="handleRemove"
		/>
		<ModalHighlightLOBWidget
			:ref="REFS_HIGHLIGHT_LOB_WIDGET_MODAL"
			@onSubmit="onSubmitModal"
			@onRemove="handleRemove"
		/>
		<ModalHighlightWidget
			:ref="REFS_HIGHLIGHT_WIDGET_MODAL"
			@onSubmit="onSubmitModal"
			@onRemove="handleRemove"
		/>
		<ModalCustomHeroBannerWidget
			:ref="REFS_CUSTOM_HERO_BANNER_WIDGET_MODAL"
			@onSubmit="onSubmitModal"
			@onRemove="handleRemove"
		/>
		<ModalHighlightBannerWidget
			:ref="REFS_HIGHLIGHT_BANNER_WIDGET_MODAL"
			@onSubmit="onSubmitModal"
			@onRemove="handleRemove"
		/>
		<BaseModalConfirmDelete
			ref="modal-remove"
			:handle-remove="deletePageWidgets.bind(null, widgetId)"
			title="Remove this widget?"
			description="By removing this, the widget will be disappeared from the list and linked items will be lost"
			@onSuccess="fetchWidgets"
		/>
	</div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import ModalProductWidget from '@/components/WidgetsStaticPage/ModalProductWidget.vue';
import ModalWYSIWYGWidget from '@/components/WidgetsStaticPage/ModalWYSIWYGWidget.vue';
import ModalAddWYSIWYG from '@/components/WidgetsStaticPage/ModalAddWYSIWYG.vue';
import ModalLOBWidget from '@/components/WidgetsStaticPage/ModalLOBWidget.vue';
import ModalHighlightLOBWidget from '@/components/WidgetsStaticPage/ModalHighlightLOBWidget.vue';
import ModalHighlightWidget from '@/components/WidgetsStaticPage/ModalHighlightWidget.vue';
import ModalCustomHeroBannerWidget from '@/components/WidgetsStaticPage/ModalCustomHeroBannerWidget.vue';
import ModalHighlightBannerWidget from '@/components/WidgetsStaticPage/ModalHighlightBannerWidget.vue';
import BaseModalConfirmDelete from '@/components/BaseModalConfirmDelete.vue';
import { WIDGET_TYPE } from '../../enums/widgets';
import { ROUTE_NAME } from '../../enums/route';

const WIDGET_FIELDS = [
	{ key: 'name', label: 'Name', class: 'col-name' },
	{ key: 'type', label: 'Type', class: 'col-type' },
	{ key: 'linkTo', label: 'Link To', class: 'col-link-to' },
	{ key: 'edit', label: '', class: 'col-edit' },
];

const WIDGET_ROUTES = {
	[WIDGET_TYPE.hero]: 'BannerHeroList',
	[WIDGET_TYPE.custom_hero]: 'BannerCustomHeroList',
	[WIDGET_TYPE.product_category]: 'BannerProductCategory',
	[WIDGET_TYPE.lob]: 'BannerLobList',
	[WIDGET_TYPE.highlight_lob]: 'BannerHighlightLobList',
	[WIDGET_TYPE.highlight]: 'BannerHighlightList',
	[WIDGET_TYPE.small]: 'BannerSmallList',
	[WIDGET_TYPE.shortcut]: 'BannerShortcutList',
	[WIDGET_TYPE.content]: 'MicrositeBannerContentList',
	[WIDGET_TYPE.brandBanner]: 'BrandBannerEdit',
	[WIDGET_TYPE.productHighlight]: 'ProductHighlightEdit',
	[WIDGET_TYPE.shop_by_brand]: 'BannerShopByBrandList',
	[WIDGET_TYPE.highlightContentBlock]: ROUTE_NAME.CONTENT_BLOCK_HIGHLIGHT_LIST,
	[WIDGET_TYPE.stripeContentBlock]: ROUTE_NAME.CONTENT_BLOCK_STRIPE_LIST,
	[WIDGET_TYPE.textWithImageContentBlock]: ROUTE_NAME.CONTENT_BLOCK_TEXT_WITH_IMAGE_LIST,
};

const REFS_PRODUCT_WIDGET_MODAL = 'modal-product-widget';
const REFS_WYSIWYG_WIDGET_MODAL = 'modal-wysiwyg-widget';
const REFS_ADD_WYSIWYG_MODAL = 'modal-add-wysiwyg';
const REFS_LOB_WIDGET_MODAL = 'modal-lob-widget';
const REFS_HIGHLIGHT_LOB_WIDGET_MODAL = 'modal-highlight-lob-widget';
const REFS_HIGHLIGHT_WIDGET_MODAL = 'modal-highlight-widget';
const REFS_CUSTOM_HERO_BANNER_WIDGET_MODAL = 'modal-custom-hero-banner-widget';
const REFS_HIGHLIGHT_BANNER_WIDGET_MODAL = 'modal-highlight-banner-widget';

export default {
	name: 'WidgetPageList',
	components: {
		ModalProductWidget,
		ModalAddWYSIWYG,
		ModalWYSIWYGWidget,
		ModalLOBWidget,
		ModalHighlightLOBWidget,
		ModalHighlightWidget,
		ModalCustomHeroBannerWidget,
		ModalHighlightBannerWidget,
		BaseModalConfirmDelete,
	},
	props: {
		isEditMode: {
			type: Boolean,
			default: false,
		},
		slug: {
			type: String,
			default: '',
		},
	},
	data() {
		return {
			WIDGET_FIELDS,
			WIDGET_TYPE,
			REFS_PRODUCT_WIDGET_MODAL,
			REFS_WYSIWYG_WIDGET_MODAL,
			REFS_ADD_WYSIWYG_MODAL,
			REFS_LOB_WIDGET_MODAL,
			REFS_HIGHLIGHT_LOB_WIDGET_MODAL,
			REFS_HIGHLIGHT_WIDGET_MODAL,
			REFS_CUSTOM_HERO_BANNER_WIDGET_MODAL,
			REFS_HIGHLIGHT_BANNER_WIDGET_MODAL,
			widgetList: [],
			page_id: this.$route.params?.id ?? null,
			widgetRefs: null,
			widgetId: null,
		};
	},
	computed: {
		...mapState('pages', {
			editPage: 'edit', //* page
			create: 'createWidget', //* widget
			edit: 'editWidget', //* widget
			list: 'listWidget', //* widget
			sort: 'sortWidget', //* widget
		}),
		isLoading() {
			return this.sort.isLoading || this.list.isLoading;
		},
	},
	watch: {
		list: {
			deep: true,
			immediate: true,
			handler(list) {
				this.widgetList = list.data;
			},
		},
	},
	async created() {
		this.widgetList = [];
		if (this.isEditMode && !this.editPage.error) {
			await this.fetchWidgets();
		}
	},
	methods: {
		...mapActions({
			getPageWidgetList: 'pages/getPageWidgetList',
			createPageWidgets: 'pages/createPageWidgets',
			updatePageWidgets: 'pages/updatePageWidgets',
			deletePageWidgets: 'pages/deletePageWidgets',
			sortPageWidgets: 'pages/sortPageWidgets',
		}),
		async fetchWidgets() {
			const params = {
				per_page: 'all',
				page_id: this.page_id,
			};

			await this.getPageWidgetList(params);
		},
		async handleUpdateOrder(sortedList) {
			this.widgetList = sortedList;
			const params = {
				page_id: this.page_id,
				page_widget_ids: this.widgetList.map((item) => item.id),
			};
			await this.sortPageWidgets(params);
		},
		handleEdit(id) {
			const editData = this.widgetList.find((item) => item.id === id);
			if (editData) {
				if (editData.type === WIDGET_TYPE.wysiwyg) {
					this.onClickOpenModal(REFS_WYSIWYG_WIDGET_MODAL, editData);
				} else if (editData.type === WIDGET_TYPE.product || editData.type === WIDGET_TYPE.products) {
					this.onClickOpenModal(REFS_PRODUCT_WIDGET_MODAL, editData);
				} else if (editData.type === WIDGET_TYPE.highlight) {
					this.onClickOpenModal(REFS_HIGHLIGHT_BANNER_WIDGET_MODAL, editData);
				} else if (editData.type === WIDGET_TYPE.custom_hero) {
					this.onClickOpenModal(REFS_CUSTOM_HERO_BANNER_WIDGET_MODAL, editData);
				} else if (editData.type === WIDGET_TYPE.highlight_lob_banner) {
					this.onClickOpenModal(REFS_HIGHLIGHT_LOB_WIDGET_MODAL, editData);
				} else if (editData.type === WIDGET_TYPE.lob) {
					this.onClickOpenModal(REFS_LOB_WIDGET_MODAL, editData);
				}
			}
		},
		handleLink(item) {
			const editData = this.widgetList.find((widget) => widget.id === item.id);
			if (editData) {
				if (item.type === WIDGET_TYPE.wysiwyg) { //* type wysiwyg
					this.onClickOpenModal(REFS_ADD_WYSIWYG_MODAL, editData);
				} else if (item.type === WIDGET_TYPE.product) {
					if (editData.data.length) { //* type product
						//* router to marketing campaign id
						const id = editData.data[0].id;
						const routeData = this.$router.resolve({
							name: ROUTE_NAME.MARKETING_CAMPAIGN_EDIT,
							params: { id },
						});
						window.open(routeData.href, '_blank');
					} else {
						//* router to marketing campaign list
						const routeData = this.$router.resolve({ name: ROUTE_NAME.MARKETING });
						window.open(routeData.href, '_blank');
					}
				} else {
					const routeName = WIDGET_ROUTES[item.type];
					if (routeName) {
						const routeData = this.$router.resolve({
							name: routeName,
							query: item.type === WIDGET_TYPE.product_category ? {} : { page_widget_id: item.id, is_visible: 1 },
						});
						window.open(routeData.href, '_blank');
					}
				}
			}
		},
		handleRemove() {
			this.$refs[this.widgetRefs].close();
			this.$refs['modal-remove'].open();
		},
		onClickOpenModal(widgetRefs, widget = null) {
			this.widgetId = widget?.id ?? null;
			this.widgetRefs = widgetRefs;
			this.$refs[this.widgetRefs].open(widget);
		},
		async onSubmitModal(data) {
			const params = {
				page_id: this.page_id,
				type: data.type,
				name: data.name,
				title_en: data.titleEN ?? data.title_en,
				title_th: data.titleTH ?? data.title_th,
				selling_type: data.sellingType ?? data.selling_type ?? null,
				content_en: data.contentEn ?? data.content_en ?? '',
				content_th: data.contentTh ?? data.content_th ?? '',
				is_display_button: data.displayButton ?? data.is_display_button,
				button_name_en: data.buttonNameEN ?? data.button_name_en,
				button_name_th: data.buttonNameTH ?? data.button_name_th,
				url_link: data.widgetLink ?? data.link, // Handle both modal formats
				is_active: data.websiteVisibility?.value === 'on' ? 1 : 0,
				is_mobile_active: data.mobileVisibility?.value === 'on' ? 1 : 0,
				short_description: data.short_description ?? data.shortDescription,
				side_toggle_type: data.side_toggle_type ?? data.sideToggleType,
				display_page: data.display_page ?? data.displayPage,
			};

			let isSuccess = false;
			const id = this.widgetId;
			if (id) {
				await this.updatePageWidgets({ id, params });
				if (this.edit.isSuccess) {
					isSuccess = true;
				}
			} else {
				await this.createPageWidgets(params);
				if (this.create.isSuccess) {
					isSuccess = true;
				}
			}
			if (isSuccess) {
				this.$refs[this.widgetRefs].close();
				await this.fetchWidgets();
			}
		},
	},
};
</script>
<style lang="scss" scoped>
::v-deep {
	.main-wrapper {
		width: auto;
		padding: 0 24px;
	}

	.link-to {
		color: $color-primary;
		cursor: pointer;

		&:hover {
			color: $color-primary;
		}
	}

	.link-status {
		font-size: rem($font-size-caption);
		color: $color-black-45;
	}

	.table-draggable {
		padding: 0 23px;

		tbody {
			td:first-child {
				border-left: 0;
			}

			td:last-child {
				border-right: 0;
			}
		}
	}
}

.add-widgets-container {
	position: relative;
	display: inline-block;

	.tooltip {
		position: absolute;
		top: -30px;
		left: 50%;
		transform: translateX(-50%);
		background-color: #333;
		color: white;
		padding: 5px 10px;
		border-radius: 4px;
		opacity: 0;
		visibility: hidden;
		transition: opacity 0.2s, visibility 0.2s;
		white-space: nowrap;
	}

	.btn-dropdown:hover + .tooltip {
		opacity: 1;
		visibility: visible;
	}
}
</style>
